"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CheckedMeterPicker = CheckedMeterPicker;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("@heroui/react");
var react_2 = require("@iconify/react");
var react_3 = require("react");
var meter_premise_config_1 = require("../../../features/apis/meter-premise-config");
/*
 * MC&LCC-3.2 — the "Checked Meter" picker.
 *
 * Erik specified this interaction in unusual detail, because it is the part of the screen someone
 * repeats dozens of times:
 *
 *   "add the '…' in a 'Checked Meter' column, when the user clicks on this, it must become a dropdown
 *    … The information displayed in this dropdown should be the meter and the premise name combined
 *    (Meter serial number in black, premise name in grey with a space in between) … The user should
 *    be able to search for either a premise or a serial number. Once the user selects one, add it
 *    below the premise name, but immediately create another dropdown and allow the user to search
 *    immediately … If the user clicks anywhere else, turn the drop-down into a '…' … Selecting the
 *    meter should be via clicking or by pressing tab (to move to the next meter), and enter to select
 *    the meter … when pressing tab, make the option become grey."
 *
 * Built by hand rather than with Autocomplete for one reason: Tab. Erik wants Tab to walk the result
 * list, and every combobox component in the library treats Tab as "leave this field" — that is what
 * Tab means everywhere else on the web, so the component is right and this screen is the exception.
 * Taking the key over means owning the list, which is what this is.
 *
 * Returning focus to the input after each pick is what makes "immediately create another dropdown"
 * true: the user types, Enter, types, Enter, without reaching for the mouse.
 */
function CheckedMeterPicker(props) {
    var clientId = props.clientId, buildingId = props.buildingId, selectedIds = props.selectedIds, onPick = props.onPick, onDismiss = props.onDismiss;
    var _a = (0, react_3.useState)(""), term = _a[0], setTerm = _a[1];
    /** Which result Tab has walked to. -1 means none — Enter then does nothing rather than guessing. */
    var _b = (0, react_3.useState)(-1), cursor = _b[0], setCursor = _b[1];
    var _c = (0, meter_premise_config_1.useLazyFetchCheckableMetersQuery)(), fetchMeters = _c[0], _d = _c[1], data = _d.data, isFetching = _d.isFetching;
    var boxRef = (0, react_3.useRef)(null);
    var inputRef = (0, react_3.useRef)(null);
    /* Debounced, because this searches the server on every keystroke. */
    (0, react_3.useEffect)(function () {
        var t = setTimeout(function () { return void fetchMeters({ clientId: clientId, buildingId: buildingId, search: term, limit: 25 }); }, 180);
        return function () { return clearTimeout(t); };
    }, [term, clientId, buildingId, fetchMeters]);
    (0, react_3.useEffect)(function () {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var options = (0, react_3.useMemo)(function () {
        var _a, _b;
        var all = (_b = (_a = data === null || data === void 0 ? void 0 : data.content) === null || _a === void 0 ? void 0 : _a.meters) !== null && _b !== void 0 ? _b : [];
        var taken = new Set(selectedIds);
        return all.filter(function (m) { return !taken.has(m.meterId); });
    }, [data, selectedIds]);
    // A new result set invalidates where Tab had got to.
    (0, react_3.useEffect)(function () { return setCursor(-1); }, [data]);
    /*
     * "If the user clicks anywhere else, turn the drop-down into a '…'". Pointerdown rather than click,
     * so the collapse happens before the click lands on whatever was underneath.
     */
    (0, react_3.useEffect)(function () {
        var onPointerDown = function (e) {
            if (boxRef.current && !boxRef.current.contains(e.target))
                onDismiss();
        };
        document.addEventListener("pointerdown", onPointerDown);
        return function () { return document.removeEventListener("pointerdown", onPointerDown); };
    }, [onDismiss]);
    var choose = function (m) {
        var _a;
        onPick(m);
        // Erik: "immediately create another dropdown and allow the user to search immediately."
        setTerm("");
        setCursor(-1);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var onKeyDown = function (e) {
        if (e.key === "Escape") {
            e.stopPropagation();
            onDismiss();
            return;
        }
        if (e.key === "Tab" && options.length) {
            // Taken over deliberately — see the note at the top of this file.
            e.preventDefault();
            setCursor(function (c) { return (e.shiftKey ? (c <= 0 ? options.length - 1 : c - 1) : (c + 1) % options.length); });
            return;
        }
        // Arrow keys do the same walk, because a keyboard user will reach for them first.
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            if (!options.length)
                return;
            e.preventDefault();
            setCursor(function (c) { return (e.key === "ArrowDown" ? (c + 1) % options.length : c <= 0 ? options.length - 1 : c - 1); });
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            // Enter with nothing walked to takes the only result, if there is exactly one.
            var pick = cursor >= 0 ? options[cursor] : options.length === 1 ? options[0] : null;
            if (pick)
                choose(pick);
        }
    };
    return ((0, jsx_runtime_1.jsxs)("div", { ref: boxRef, className: "flex w-[22rem] max-w-full flex-col gap-1", children: [(0, jsx_runtime_1.jsx)(react_1.Input, { ref: inputRef, size: "sm", variant: "bordered", autoFocus: true, "aria-label": "Search meters by serial number or premise", placeholder: "Search a serial number or premise\u2026", value: term, onValueChange: setTerm, onKeyDown: onKeyDown, startContent: (0, jsx_runtime_1.jsx)(react_2.Icon, { icon: "solar:magnifer-linear", className: "text-sm text-gray-400" }), endContent: isFetching ? (0, jsx_runtime_1.jsx)(react_1.Spinner, { size: "sm" }) : null }), (0, jsx_runtime_1.jsx)("div", { className: "max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm", children: options.length ? ((0, jsx_runtime_1.jsx)(react_1.Listbox, { "aria-label": "Matching meters", variant: "flat", onAction: function (key) {
                        var m = options.find(function (o) { return o.meterId === String(key); });
                        if (m)
                            choose(m);
                    }, children: options.map(function (m, i) {
                        var _a, _b, _c, _d;
                        return ((0, jsx_runtime_1.jsx)(react_1.ListboxItem, { textValue: "".concat(m.serialNumber, " ").concat((_b = (_a = m.premiseName) !== null && _a !== void 0 ? _a : m.areaName) !== null && _b !== void 0 ? _b : ""), children: (0, jsx_runtime_1.jsxs)("span", { className: "flex items-center gap-2", children: [(0, jsx_runtime_1.jsx)("span", { className: "font-mono ".concat(i === cursor ? "text-gray-400" : "text-gray-900"), children: m.serialNumber }), (0, jsx_runtime_1.jsx)("span", { className: "text-gray-400", children: (_d = (_c = m.premiseName) !== null && _c !== void 0 ? _c : m.areaName) !== null && _d !== void 0 ? _d : "—" }), m.tenantName ? (0, jsx_runtime_1.jsxs)("span", { className: "truncate text-[11px] text-gray-400", children: ["\u00B7 ", m.tenantName] }) : null, m.meterType ? ((0, jsx_runtime_1.jsx)(react_1.Chip, { size: "sm", variant: "flat", className: "ml-auto h-5", children: m.meterType })) : null] }) }, m.meterId));
                    }) })) : ((0, jsx_runtime_1.jsx)("p", { className: "px-3 py-2 text-[12px] text-gray-400", children: isFetching ? "Searching…" : "No meters match." })) }), (0, jsx_runtime_1.jsx)("p", { className: "text-[11px] text-gray-400", children: "Tab walks the list, Enter selects. Escape closes." })] }));
}
